import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import sqftEventManager from "../bvcomponents/sqft/components/canvas/store/sqftEventManager";
import store from "../store";
import Config from "../config";
import {getRoomLayouts} from "../bvcomponents/look/lookNavigation/lookFunctions";
import {isProd} from "./fabricatorHardcode";
import {getApplyToLogic, getShowMatchRadio} from "../bvcomponents/look/lookNavigation/lookNavigationFunctions";

export const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export const useIsMounted = () => {
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);
  return useCallback(() => isMountedRef.current, []);
};

/**
 * Renders a real DOM element (a div by default) and exposes an isMounted() getter to its children.
 *
 * It has to render an actual element (and forward the ref) because parents like react-virtualized's
 * CellMeasurer clone the child and attach a ref to it in order to measure the node. A plain function
 * component cannot receive a ref, which both triggers a React warning and breaks the measurement.
 */
export const IsMountedElement = React.forwardRef((props, ref) => {
  const {as: Tag = 'div', children, ...rest} = props;
  const isMountedComponent = useIsMounted();
  return (
    <Tag ref={ref} {...rest}>
      {typeof children === 'function' ? children(isMountedComponent) : children}
    </Tag>
  );
});

IsMountedElement.displayName = 'IsMountedElement';

export const useOutsideClick = (ref, callback, useSQFTCanvas = false) => {
  const handleClick = e => {
    if (ref.current && !ref.current.contains(e.target)) {
      callback(e);
    }
  };

  const handleClickThroughCanvasEvents = (mousePos, e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      callback(e);
    }
    return false;
  };

  useEffect(() => {
    if (useSQFTCanvas) {
      const sender = "helperOutsideClickHook";

      document.addEventListener(sqftEventManager.events.CLICK, handleClick);
      //document.addEventListener(sqftEventManager.events.TOUCH_START, handleClick);
      sqftEventManager.addEvent(
        sqftEventManager.events.MOUSE_DOWN,
        handleClickThroughCanvasEvents,
        sender,
        sqftEventManager.priorities.OVERALL
      );

      return () => {
        document.removeEventListener(sqftEventManager.events.CLICK, handleClick);
        //document.removeEventListener(sqftEventManager.events.TOUCH_START, handleClick);
        sqftEventManager.removeEvent(sender, sqftEventManager.events.MOUSE_DOWN);
      };
    }

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, callback, useSQFTCanvas]);
};

export const useOutsideClickSimple = (ref, callback, excludedClassNames = null) => {
  const handleClick = event => {
    if (ref.current && !ref.current.contains(event.target)) {
      if (excludedClassNames && excludedClassNames.some(className => {
        return event.target.classList.contains(className) || event.target.closest("."+className)
      })) {
        return
      }
      callback(event);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  });
};

export const useProgressiveImage = (imageUrl) => {

  const [thumbnailLoaded, setThumbnailLoaded] = useState(false)
  const isMounted = useIsMounted();

  useEffect(() => {
    const image = new Image();
    image.onload = () => {
      if (isMounted()) {
        setThumbnailLoaded(true)
      }
    };
    image.onerror = () => {
      if (isMounted()) {
        setThumbnailLoaded(true)
      }
    };
    try {
      image.src = imageUrl;
      if (image.complete) {
        if (isMounted()) {
          setThumbnailLoaded(true)
        }
      }
    } catch (e) {
      if (isMounted()) {
        setThumbnailLoaded(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl])

  return thumbnailLoaded
}

// let requestInProgressTimeoutId = null;

// export const useFeaturesChecker = (featureGroup,
//                                    visualizersLoadedLength,
//                                    wizardActiveItem,
//                                    wizardGroup,
//                                    wizardMissingFeatures,
//                                    navItems) => {
//
//   const [featuresLoaded, setFeaturesLoaded] = useState(featureGroup ? featureGroup.allFeaturesReceived : false)
//   const isMounted = useIsMounted();
//
//   // Utility to recursively wait until allFeaturesReceived becomes true
//   const waitForFeaturesToBeReceived = () => {
//     return new Promise((resolve) => {
//       const callRecursiveFeaturesCheck = () => {
//         console.log("check features", featureGroup);
//         if (featureGroup.allFeaturesReceived) {
//           clearTimeout(requestInProgressTimeoutId);
//           store.dispatch(updateNavigationCount());
//           resolve();
//         } else {
//           requestInProgressTimeoutId = setTimeout(callRecursiveFeaturesCheck, 1000); // Check again in 1 second
//         }
//       };
//       requestInProgressTimeoutId = setTimeout(callRecursiveFeaturesCheck, 1000); // Initial check after 1s
//     });
//   };
//
//   const checkFeatures = async () => {
//     if (!featureGroup || !visualizersLoadedLength) {
//       return false
//     }
//     if (!featureGroup.allFeaturesReceived) {
//       if (featureGroup.requestInProgress) {
//         await waitForFeaturesToBeReceived();
//       } else {
//         await checkIfLookFeaturesReceived(featureGroup);
//         store.dispatch(updateNavigationCount());
//       }
//     }
//     debugger;//eslint-disable-line
//     // only request first time!! then only on scroll
//     if (wizardActiveItem && wizardGroup && navItems) {
//       const key = getWizardMissingKey(wizardActiveItem, wizardGroup);
//       if (featureGroup && shouldUseNewWizardFeaturesRequestsLogic(wizardGroup, featureGroup)
//         && !wizardMissingFeatures[key]) {
//         setFeaturesLoaded(false);
//         await getMissingWizardFeatures(navItems, wizardGroup, featureGroup);
//       }
//     }
//     if (isMounted()) {
//       setFeaturesLoaded(true);
//     }
//   };
//
//   useEffect(() => {
//     checkFeatures();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [featureGroup, visualizersLoadedLength, wizardActiveItem, wizardGroup, navItems])
//
//   return featuresLoaded
// }

export const useShowRoomLayoutsButton = (activePreset, isMobile, visualizersLoadedLength) => {
  return useMemo(() => {
    if (!visualizersLoadedLength) {
      return false
    }
    if (isMobile) {
      return false
    }
    if (Config.isVisualizerManagement()) {
      return true
    }
    if (isProd()) { // hided room scenes for production URLs
      return false
    }
    return getRoomLayouts().length > 1
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePreset, visualizersLoadedLength, isMobile])
};

let debounceTimerId = null;
export default function useDebounce(effect, dependencies, delay) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callback = useCallback(effect, dependencies);

  useEffect(() => {
    debounceTimerId = setTimeout(callback, delay);
    return () => clearTimeout(debounceTimerId);
  }, [callback, delay]);
}

export const checkIfNeedToUpdateMatchAll = (featureGroup, wizardGroup) => {
  const useAllWallsApplyLogic = getShowMatchRadio(featureGroup.activeFeature);
  if (useAllWallsApplyLogic) {
    const applyToLogic = getApplyToLogic(featureGroup.activeFeature);
    const {setApplyToFunction, applyToArrayAll, currentApplyToArray} = applyToLogic;
    const checked = currentApplyToArray.includes(featureGroup.activeFeature.partId);
    // console.log("UPDATED!", );
    if ((checked && wizardGroup.matchAllDisabled) || (!wizardGroup.matchAllDisabled && !checked)) {
      if (checked) {
        store.dispatch(setApplyToFunction([], true, applyToLogic.type))
      } else {
        store.dispatch(setApplyToFunction([...applyToArrayAll], true, applyToLogic.type)) // update checkbox
      }
    }
  }
}

export const useUpdateMatchAllButton = (wizardGroup, featureGroup) => {
  const prevWizardGroup = usePrevious(wizardGroup);
  const prevFeatureGroup = usePrevious(featureGroup);

  useEffect(() => {
    if (!featureGroup || !featureGroup.activeFeature || !wizardGroup || (wizardGroup.groupName !== featureGroup.activeFeature.dbFeature.groupName)) {
      return
    }
    if (wizardGroup && prevWizardGroup && (prevWizardGroup.id === wizardGroup.id)
      && featureGroup && prevFeatureGroup && (featureGroup.partId === prevFeatureGroup.partId)) { // feature was changed but wg is still the same
      return
    }
    if (wizardGroup && prevWizardGroup && prevWizardGroup.mergedGroups && wizardGroup.mergedGroups
      && (prevWizardGroup.mergedGroups[0] === wizardGroup.mergedGroups[0])) { // merged groups different group
      return
    }
    // if (featureGroup && prevFeatureGroup && (featureGroup.partId === prevFeatureGroup.partId)) { // feature was changed but wg is still the same
    //   return
    // }
    checkIfNeedToUpdateMatchAll(featureGroup, wizardGroup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardGroup, featureGroup]);
}
